// Stacked bars, vertical (a value per period) or horizontal (a value per category).
//
// One shared scale across every bar — the design system's rule for comparison charts, and
// the whole point of a stacked bar is that column heights are comparable.
//
// props:
//   data:        [{ bucket, <seriesKey>: number, ... }]
//   series:      [string]  — stack order, first at the base
//   colors:      { [seriesKey]: color } — optional; see below
//   orientation: 'vertical' | 'horizontal'
//   format:      'number' | 'currency' | 'percent'
//
// `colors` keys by series NAME, not position. Without it each chart indexes the palette by
// its own ordering, so a brand that is second here and fourth in the donut beside it draws
// in two different colours — which is exactly how a reader mismatches a series to a legend.
// Callers rendering more than one chart over the same members should pass one shared map.

import { SERIES as PALETTE } from '../../../shared/styles/tokens.js';
import { T } from '../../../shared/styles/tokens.js';
import { fmt } from '../format.js';

export default function StackedBarChart({
  data, series, colors, orientation = 'vertical', format = 'currency',
  height = 260,     // vertical: plot area height
  barSize = 20,     // horizontal: thickness of each bar
}) {
  if (!data?.length || !series?.length) return <p className="muted">No data.</p>;

  const colourFor = (i, key) => colors?.[key] || PALETTE[i % PALETTE.length];

  const totals = data.map(d => series.reduce((n, k) => n + (Number(d[k]) || 0), 0));
  const max = Math.max(...totals, 1);

  const legend = (
    <div className="chart-legend">
      {series.map((k, i) => (
        <span key={k} className="legend-item">
          <i style={{ background: colourFor(i, k) }} /> {k}
        </span>
      ))}
    </div>
  );

  if (orientation === 'horizontal') {
    return (
      <div className="chart">
        {legend}
        <div style={{ display: 'flex', flexDirection: 'column', gap: Math.round(barSize / 2) }}>
          {data.map((d, row) => (
            <div key={d.bucket} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 108, flexShrink: 0, fontSize: T.tSmall, color: T.text2,
                textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }} title={d.bucket}>{d.bucket}</span>
              <span style={{
                flex: 1, display: 'flex', height: barSize, borderRadius: 2,
                overflow: 'hidden', background: T.surface2,
              }}>
                {series.map((k, i) => {
                  const v = Number(d[k]) || 0;
                  if (!v) return null;
                  return (
                    <span key={k} title={`${d.bucket} · ${k}: ${fmt(v, format)}`}
                      style={{ width: `${(v / max) * 100}%`, background: colourFor(i, k) }} />
                  );
                })}
              </span>
              <span className="o-num" style={{ width: 84, fontSize: 11, textAlign: 'right' }}>
                {fmt(totals[row], format)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Vertical: CSS columns rather than SVG, so labels stay selectable and the bars reflow
  // with the panel instead of being pinned to a viewBox.
  return (
    <div className="chart">
      {legend}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 10, height,
        borderBottom: `1px solid ${T.rule}`, paddingBottom: 2,
      }}>
        {data.map((d, col) => (
          <div key={d.bucket} style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'flex-end', height: '100%',
          }}>
            <span className="o-num" style={{ fontSize: 10, marginBottom: 3, color: T.text3 }}>
              {fmt(totals[col], format)}
            </span>
            <span style={{
              width: '100%', maxWidth: 54, display: 'flex', flexDirection: 'column-reverse',
              height: `${(totals[col] / max) * 100}%`, borderRadius: '2px 2px 0 0',
              overflow: 'hidden', background: T.surface2,
            }}>
              {series.map((k, i) => {
                const v = Number(d[k]) || 0;
                if (!v) return null;
                return (
                  <span key={k} title={`${d.bucket} · ${k}: ${fmt(v, format)}`}
                    style={{ height: `${(v / (totals[col] || 1)) * 100}%`, background: colourFor(i, k) }} />
                );
              })}
            </span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        {data.map(d => (
          <span key={d.bucket} style={{
            flex: 1, textAlign: 'center', fontSize: 11, color: T.text3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{d.bucket}</span>
        ))}
      </div>
    </div>
  );
}
